import type {TokenGetter} from '@myparcel-frontend/service-utils';
import type {Plugin} from 'vue';
import type {VueI18nInstance} from 'vue-i18n';
import {useAddressService} from '@myparcel-frontend/address-service';
import {useBolEcommerceService} from '@myparcel-frontend/bol-ecommerce-service';
import {
  useCoreApiService,
  useShipmentCapabilitiesService,
  useShipmentPricingService,
} from '@myparcel-frontend/core-api-service';
import {useCscartEcommerceService} from '@myparcel-frontend/cscart-ecommerce-service';
import {useDelegationService} from '@myparcel-frontend/delegation-service';
import {useGenericEcommerceService} from '@myparcel-frontend/generic-ecommerce-service';
import {useIamService} from '@myparcel-frontend/iam-service';
import {useLightspeedEcommerceService} from '@myparcel-frontend/lightspeed-ecommerce-service';
import {useOrderService} from '@myparcel-frontend/order-service';
import {usePrestaEcommerceService} from '@myparcel-frontend/presta-ecommerce-service';
import {usePrintingService} from '@myparcel-frontend/printing-service';
import {useProductService} from '@myparcel-frontend/product-service';
import {usePurchaseOrderService} from '@myparcel-frontend/purchase-order-service';
import {useRuleService} from '@myparcel-frontend/rule-service';
import {useSdk} from '@myparcel-frontend/sdk';
import {useShopifyEcommerceService} from '@myparcel-frontend/shopify-ecommerce-service';
import {useWooEcommerceService} from '@myparcel-frontend/woo-ecommerce-service';
import {watch} from 'vue';
import {useEnvironmentStore} from '../../env/useEnvironmentStore';

export const createClients = (token: string | TokenGetter): Plugin => {
  const iamService = useIamService();
  const coreApiService = useCoreApiService();
  const delegationService = useDelegationService();
  const genericEcommerceService = useGenericEcommerceService();
  const orderService = useOrderService();
  const printingService = usePrintingService();
  const productService = useProductService();
  const bolEcommerceService = useBolEcommerceService();
  const shopifyEcommerceService = useShopifyEcommerceService();
  const wooEcommerceService = useWooEcommerceService();
  const lightspeedEcommerceService = useLightspeedEcommerceService();
  const prestaEcommerceService = usePrestaEcommerceService();
  const cscartEcommerceService = useCscartEcommerceService();
  const addressService = useAddressService();
  const shipmentCapabilitiesService = useShipmentCapabilitiesService();
  const purchaseOrderService = usePurchaseOrderService();
  const ruleService = useRuleService();
  const shipmentsPricingService = useShipmentPricingService();
  const {setAcceptLanguageHeader} = useSdk();

  const {environment} = useEnvironmentStore();

  const initApiServiceClients = (token: string, locale: string) => {
    addressService.initClient({
      token,
      locale,
      environment: environment.environment,
    });
    bolEcommerceService.initClient({
      token,
      locale,
      environment: environment.environment,
    });
    coreApiService.initClient({
      token,
      locale,
      environment: environment.environment,
      baseUrl: environment.apiUrl,
    });
    delegationService.initClient({
      token,
      locale,
      environment: environment.environment,
    });
    genericEcommerceService.initClient({
      token,
      locale,
      environment: environment.environment,
    });
    iamService.initClient({
      token,
      locale,
      environment: environment.environment,
    });
    lightspeedEcommerceService.initClient({
      token,
      locale,
      environment: environment.environment,
    });
    prestaEcommerceService.initClient({
      token,
      locale,
      environment: environment.environment,
    });
    cscartEcommerceService.initClient({
      token,
      locale,
      environment: environment.environment,
    });
    orderService.initClient({
      token,
      locale,
      environment: environment.environment,
    });
    printingService.initClient({
      token,
      locale,
      environment: environment.environment,
    });
    purchaseOrderService.initClient({
      token,
      locale,
      environment: environment.environment,
    });
    productService.initClient({
      token,
      locale,
      environment: environment.environment,
    });
    ruleService.initClient({
      token,
      locale,
      environment: environment.environment,
    });
    shipmentCapabilitiesService.initClient({
      token,
      locale,
      environment: environment.environment,
    });
    shipmentsPricingService.initClient({
      token,
      locale,
      environment: environment.environment,
    });
    shopifyEcommerceService.initClient({
      token,
      locale,
      environment: environment.environment,
    });
    wooEcommerceService.initClient({
      token,
      locale,
      environment: environment.environment,
    });
  };

  const addLanguageToServices = (newLocale: string) => {
    addressService.setAcceptLanguageHeader(newLocale);
    bolEcommerceService.setAcceptLanguageHeader(newLocale);
    coreApiService.setAcceptLanguageHeader(newLocale);
    delegationService.setAcceptLanguageHeader(newLocale);
    genericEcommerceService.setAcceptLanguageHeader(newLocale);
    iamService.setAcceptLanguageHeader(newLocale);
    lightspeedEcommerceService.setAcceptLanguageHeader(newLocale);
    prestaEcommerceService.setAcceptLanguageHeader(newLocale);
    cscartEcommerceService.setAcceptLanguageHeader(newLocale);
    orderService.setAcceptLanguageHeader(newLocale);
    printingService.setAcceptLanguageHeader(newLocale);
    productService.setAcceptLanguageHeader(newLocale);
    purchaseOrderService.setAcceptLanguageHeader(newLocale);
    ruleService.setAcceptLanguageHeader(newLocale);
    shipmentCapabilitiesService.setAcceptLanguageHeader(newLocale);
    shipmentsPricingService.setAcceptLanguageHeader(newLocale);
    shopifyEcommerceService.setAcceptLanguageHeader(newLocale);
    wooEcommerceService.setAcceptLanguageHeader(newLocale);
    setAcceptLanguageHeader(newLocale);
  };

  return {
    install(app) {
      const i18n = app.config.globalProperties.$i18n as VueI18nInstance | undefined;

      if (!i18n) {
        throw new Error('i18n instance is not available in app config');
      }

      initApiServiceClients(token, i18n.locale);

      watch(
        () => i18n.locale,
        (newLocale) => {
          addLanguageToServices(newLocale);
        },
        {
          immediate: false,
        },
      );
    },
  };
};
